import { LitElement, PropertyValueMap, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { GetPublicOrganizationResponse } from '../types';
import { Menu } from '@material/mwc-menu';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { getVisibleTextColor } from '../helpers/colors';
import { isDefined } from '../helpers/utils';
import organizationInfo from '../helpers/state/organization-info';
import { clearOrgCookie, setOrgCookie } from '../helpers/auth-cookie';
import { palette } from '../style/variables';

interface TeamEntryOption {
    name: string;
    logo: string;
    id: string;
    primary_color: string;
    secondary_color: string;
    validated_custom_url?: string;
};

@customElement('team-switch-menu')
export class TeamSwitchMenu extends LitElement {


    @query('#cornerMenu')
    cornerMenu!: Menu;

    @query('#menuConnect')
    menuConnect!: HTMLDivElement;

    @property()
    amplitude_callsite: string = '';

    @state()
    organizations: GetPublicOrganizationResponse[] = [];

    @state()
    entries: TeamEntryOption[] = [];

    @state()
    menuOpen = false;

    @state()
    hideCreateTeamOption = false;

    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindOrganizationState = new StateController(this, organizationInfo);

    toggleMenuOpen() {
        // this.cornerMenu.open = true;
        this.menuOpen = !this.menuOpen;
    }

    // if this.organizations changes, create the entries
    async updated(changedProperties: PropertyValueMap<TeamSwitchMenu>) {
        if (changedProperties.has('organizations')) {
            await this.createEntries();
        }
    }

    private async postOrganizationUpgrade(organization_entry: TeamEntryOption | undefined) {
        // set local storage and cross-domain cookie
        if (!organization_entry) {
            localStorage.removeItem('organization_id');
            clearOrgCookie();
        } else {
            localStorage.setItem('organization_id', organization_entry.id);
            setOrgCookie(organization_entry.id);
            // in beta
            const isInBeta = window.location.href.includes('betamap.proxi.co');
            if (organization_entry.validated_custom_url && !isInBeta) {
                // replace the url base with the custom url
                const url = window.location.href;
                const urlParts = url.split('/');
                const baseUrl = urlParts[2].toLowerCase();
                const newUrl = url.replace(baseUrl, organization_entry.validated_custom_url);
                // set the new url
                window.location.href = newUrl;
            }
        }
        // refresh the page
        window.location.reload();
    }

    private async createEntries() {
       // iterate through organizations. if the organization does not have a parent, add it to the entries
        this.entries = this.organizations
            .map(org => ({
                name: org.name,
                logo: org.logo,
                id: org._id.$oid,
                primary_color: org.primary_color,
                secondary_color: org.secondary_color,
                validated_custom_url: isDefined(org.cname) && org.cname.validated ? org.cname.custom_url : undefined,
            }));
    }

    onClickCreateTeam() {
        this.dispatchEvent(new CustomEvent('on-start-create-team'));
    }

    protected render() {
        const org_background_color = organizationInfo.organization?.primary_color ?? 'var(--main-primary-color)';
        const org_text_color = organizationInfo.organization?.secondary_color ?? getVisibleTextColor(palette.primaryBlue) ?? 'white';
        return html`
            <div id="menuConnect" class="flexContainer" style="background-color: white; color: ${org_background_color}; ${this.menuOpen ? 'border-radius: 8px 8px 0 0' : ''}" @click=${this.toggleMenuOpen}>
                <div class="flexContainer flexGap currentSelection">
                    <account-avatar backgroundColor=${org_background_color} textColor=${org_text_color} ?isTeam=${isDefined(organizationInfo.organization)} size="md">${organizationInfo.organization?.name?.charAt(0) ?? accountInfo.fullName.charAt(0)}</account-avatar>
                    <div class="selectionName" style="color: var(--main-primary-color)">
                        <h4 style="margin: 0">${organizationInfo.organization?.name ?? 'Personal Workspace'}</h4>
                    </div>
                </div>
                ${this.organizations.length > 0 ? html`
                    ${this.menuOpen ? html`
                        <mwc-icon>expand_less</mwc-icon>
                    ` : html`
                        <mwc-icon>expand_more</mwc-icon>
                    `}
                ` : ''}
            </div>
            ${this.menuOpen ? html`
                <hr class="divider">
                <div class="dropdown">
                    <div class="divider"></div>
                    ${organizationInfo.organization && accountInfo.migrated_from_personal !== true ? html`
                        <div class="flexContainer flexGap teamOption" @click=${async () => await this.postOrganizationUpgrade(undefined)}>
                            <span class="org-name">Personal Workspace</span>
                            <account-avatar backgroundColor="var(--theme-white-color)" textColor="var(--theme-input-color)" size="sm">${accountInfo.fullName.charAt(0)}</account-avatar>
                        </div>
                        <hr class="divider">
                    ` : ''}
                    ${this.entries
                        .filter((entry) => entry.id !== organizationInfo.organization?._id.$oid)
                        .map((organizationEntry, index) => html`
                        <div class="flexContainer flexGap teamOption" @click=${async () => await this.postOrganizationUpgrade(organizationEntry)}>
                            <span class="org-name">${organizationEntry.name}
                            </span>
                            <account-avatar isTeam backgroundColor="${organizationEntry.primary_color}" textColor="${organizationEntry.secondary_color}" size="sm">${organizationEntry.name.charAt(0)}</account-avatar>
                        </div>
                        <hr class="divider">
                    `)}
                    ${!this.hideCreateTeamOption ? html`
                        <div class="flexContainer flexGap newTeam" @click=${this.onClickCreateTeam}>
                            <mwc-icon>add</mwc-icon>
                            <span>Create a new workspace</span>
                        </div>
                    ` : ''}
                </div>
            ` : ''}
        `;
    }

    static get styles() {
        return css`
            :host {
                --mdc-typography-subtitle1-font-size: 12px;
                --mdc-list-vertical-padding: 0px;
                --mdc-menu-max-width: calc(20vw - 40px);
            }

            .dropdown {
                background-color: white;
                border-radius: 0 0 8px 8px;
                z-index: 100;
                width: 100%;
                max-height: 50vh;
                overflow-y: auto;
            }

            .divider {
                margin: 0;
                background-color: var(--theme-lightest-color);
            }

            .teamOption {
                padding: 0.5rem;
                cursor: pointer;
                justify-content: space-between;
            }

            .child {
                padding-left: 1rem;
            }

            #menuConnect {
                cursor: pointer;
                justify-content: space-between;
                padding: 0.5rem;
                box-shadow: 0px 2px 2px -2px rgba(31, 27, 45, 0.08),
                    0px 4px 12px rgba(31, 27, 45, 0.08);
                border-radius: 8px;
            }

            .flexGap {
                gap: 0.75rem;
            }

            .org-name {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .orgListItem {
                padding-top: 0.5rem;
                padding-bottom: 0.5rem;
            }

            .newTeam {
                background-color: var(--theme-lightest-color);
                text-align: center;
                padding: 0.5rem;
                gap: 0.25rem;
                justify-content: center;
                cursor: pointer;
            }

            .createListItem {
                justify-content: center;
                background-color: #F7F8F9;
                font-weight: 500;
            }

            .flexContainer {
                display: flex;
                align-items: center;
            }

            .profileDetails {
                display: flex;
                flex-direction: column;
                margin-left: 0.5rem;
            }

            @media screen and (min-width: 901px) {
                .desktopOnly {
                    display: block;
                }

                .mobileOnly {
                    display: none;
                }

                .desktopOnly.flexContainer {
                    display: flex;
                }
            }

            @media screen and (max-width: 900px) {
                .desktopOnly {
                    display: none;
                }

                .mobileOnly {
                    display: flex;
                }

                .mobileOnly.flexContainer {
                    display: flex;
                }
            }
        `;
    }
}

